// Local user preferences (display/appearance) backed by localStorage, with a
// tiny pub/sub so any component can react live via usePref(). These are the
// real controls behind the Settings → Display tab (mockup: settings.html); they
// are applied for real (tree rendering, root attributes/vars), not decoration.
import { useEffect, useState } from 'react';

const KEY = 'rmdv_prefs';

export const PREF_DEFAULTS = {
  accent: 'azure',        // azure | indigo | green | violet
  density: 'compact',     // compact | comfortable
  reduceMotion: false,
  showHumanIds: true,
  statusDots: true,
  // #1032: closed by default — a first-run user reclaims the space; opening it is
  // then a deliberate action. This only changes the FIRST-RUN default — a returning
  // user's own explicitly-saved true/false (already written into `rmdv_prefs`) is
  // read back as-is by the merge in read() below and is never overwritten here.
  assistantPanel: false,

  // Description editor (#333): 'mdxeditor' — the markdown-native editor is the
  // ONLY user-facing editor since the markdown-native cutover (stored format is
  // markdown; TinyMCE is a hidden legacy path with no UI switch and would write
  // HTML into the markdown store). Kept as a pref so tests can pin the legacy
  // path; read() coerces any stale persisted 'tinymce' back to 'mdxeditor'.
  editor: 'mdxeditor',
  // Requirements tree — per-user project scope (#125 / lazy-tree). `visibleProjects` is
  // an array of project ids to show; [] = "all projects I can view". When more than one
  // is visible the tree groups them under top-level project nodes labelled with
  // `projectPrefix` + the project name (configurable; PRJ- for now).
  visibleProjects: [],
  projectPrefix: 'PRJ-',
  dashboardProject: '',
};

// Accent hues map to the runtime --accent override (light scheme values; the
// dark scheme keeps its indigo unless overridden — azure is the brand default).
export const ACCENTS = {
  azure: 'hsl(214 92% 48%)',
  indigo: 'hsl(248 80% 62%)',
  green: 'hsl(160 70% 38%)',
  violet: 'hsl(280 60% 55%)',
};

function read() {
  try {
    const p = { ...PREF_DEFAULTS, ...(JSON.parse(localStorage.getItem(KEY)) || {}) };
    // Migration: the TinyMCE switch existed briefly on dev (#333); a persisted
    // 'tinymce' choice would now silently write HTML into the markdown store,
    // with no UI left to escape it. Coerce on load (setPref still honours
    // 'tinymce' for tests exercising the legacy path).
    if (p.editor === 'tinymce') p.editor = 'mdxeditor';
    return p;
  }
  catch { return { ...PREF_DEFAULTS }; }
}

const listeners = new Set();
let cache = read();

export function getPrefs() { return cache; }
export function getPref(k) { return cache[k]; }

export function setPref(k, v) {
  cache = { ...cache, [k]: v };
  try { localStorage.setItem(KEY, JSON.stringify(cache)); } catch { /* ignore */ }
  listeners.forEach((fn) => fn(cache));
}

export function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }

// Subscribe to the whole prefs object.
export function usePrefs() {
  const [p, setP] = useState(cache);
  useEffect(() => subscribe(setP), []);
  return p;
}

// Subscribe to one key.
export function usePref(k) {
  const [v, setV] = useState(cache[k]);
  useEffect(() => subscribe((p) => setV(p[k])), [k]);
  return v;
}
